import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { userAPI } from '../services/api'
import CertifiedBadge from './CertifiedBadge'
import './UserMenu.css'

function UserMenu() {
  const { user, logout } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [badgeSummary, setBadgeSummary] = useState(null)
  const menuRef = useRef(null)
  const navigate = useNavigate()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleLogout = async () => {
    await logout()
    setIsOpen(false)
    navigate('/')
  }

  useEffect(() => {
    if (!user?.id) return
    userAPI.get(user.id)
      .then((data) => setBadgeSummary(data?.badges || null))
      .catch(() => setBadgeSummary(null))
  }, [user?.id])

  /* Founding + verified are shown via CertifiedBadge next to the username */
  const menuBadges = [
    badgeSummary?.first_contribution ? 'First Contribution' : null,
    badgeSummary?.top_contributor ? 'Top Contributor' : null,
    badgeSummary?.forum_certified && !badgeSummary?.founding_contributor ? 'Forum Certified' : null,
  ].filter(Boolean)

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="user-menu-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <svg
          className="user-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-header">
            <div className="user-menu-header-top">
              <span className="user-menu-username">{user?.username}</span>
              <CertifiedBadge user={user} />
              {user?.role && (
                <span className={`user-menu-role-badge user-menu-role-badge-${user.role}`}>
                  {user.role}
                </span>
              )}
            </div>
            <span className="user-menu-email">{user?.email}</span>
            {menuBadges.length > 0 && (
              <div className="user-menu-badges">
                {menuBadges.map((badge) => (
                  <span key={badge} className="user-menu-badge-pill">{badge}</span>
                ))}
              </div>
            )}
          </div>
          <div className="user-menu-divider"></div>
          <Link
            to="/dashboard"
            className="user-menu-item"
            onClick={() => setIsOpen(false)}
          >
            Dashboard
          </Link>
          <Link
            to={`/users/${user?.id}`}
            className="user-menu-item"
            onClick={() => setIsOpen(false)}
          >
            Profile
          </Link>
          {user?.role === 'admin' && (
            <Link
              to="/admin"
              className="user-menu-item"
              onClick={() => setIsOpen(false)}
            >
              Admin
            </Link>
          )}
          <div className="user-menu-divider"></div>
          <button
            className="user-menu-item user-menu-item-button"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      )}
    </div>
  )
}

export default UserMenu
